import { getFramework, type IFramework } from '@/framework';
import { BaseLayerViewType, type ILayerProps, type IVectorLayerProps } from '@/version-types';
import { Events } from '../../constants';
import type { IEdgelessVectorLayer, SVGPreviewRequest } from '../../types';
import { collectVectorLayers } from '../../utils';
import type { VectorLayerSVGPreviewItem } from './type';

export class VectorLayerSVGPreviewPlugin {
    private readonly framework: IFramework;

    /**
     * manager 内部按图层 ID 维护的可变 Preview 缓存。
     *
     * ATTENTION: Map 方便 begin、flush 和 release 对单个图层增量修改，但 set/delete 不会改变 Map
     * 自身的引用，因此不能直接作为 useSyncExternalStore 的 snapshot。
     */
    private readonly previewCache = new Map<string, VectorLayerSVGPreviewItem>();

    /**
     * 保存当前动画帧尚未构建的每个 Vector Layer 最新数据。
     *
     * 连续交互在一帧内可能发出多次 update，Map 会以最后一次数据覆盖旧数据，使每个
     * 图层每帧最多构建一次 SVG。Map 同时允许 Group Resize 在同一帧累积多个子图层。
     */
    private readonly pendingLayers = new Map<string, ILayerProps>();

    /**
     * 用于保存当前真正被 SVG Preview 接管的 Vector Layer ID。
     */
    private readonly active = new Set<string>();

    /**
     * 保存交互已结束、但仍在等待最新正式 drawable 完成的 Vector Layer ID。
     *
     * commit 时不能立即释放 SVG Preview，否则异步图片构建期间会露出旧 drawable。
     * DrawableSettled 只会释放位于此集合的图层，避免交互进行中的旧异步任务误关 Preview。
     */
    private readonly waitingForDrawable = new Set<string>();

    /**
     * 保存订阅者回调函数。
     * 目前主要服务于 React Stage 订阅者，通知其更新 SVG 预览列表。
     */
    private readonly subscribers = new Set<() => void>();

    /**
     * 保存已发布给 React Stage 的稳定 Vector SVG Preview 快照。
     *
     * useSyncExternalStore 使用引用相等性判断状态是否变化：previewCache 作为可变 Map 始终
     * 保持同一引用，React 无法感知其内部变化；如果在 getter 中每次把 Map 转成新数组，
     * 又会让未变更的 snapshot 每次都获得新引用。因此只在 publish() 时替换本数组，保证
     * 状态未变时引用稳定，发布新状态时引用更新。
     */
    private previews: VectorLayerSVGPreviewItem[] = [];

    private animationFrame = 0;

    /**
     * 已预约的 flush 宏任务 ID；0 表示当前没有等待执行的 flush。
     *
     * 含文字的 Vector 组合需要在构建时同步执行 TextBuffer 排版，部分字形还要走 Canvas measureText。
     * 如果这些工作占满 requestAnimationFrame 回调，浏览器就无法及时绘制本帧的选框和拖拽反馈。
     * 因此含文字的组合改为 rAF 之后再经 setTimeout 落到后续宏任务，让浏览器先完成当前帧绘制；
     * 无文字内容的 Vector 保持原有 rAF 内直接 flush，不引入额外延迟。
     */
    private flushTimer = 0;

    /**
     * 当前待构建队列中是否包含需要排版的文字。
     *
     * 一帧内可能同时累积无文字 Vector 与含文字 Vector（例如 Group Resize）。只要其中一个需要
     * 文字排版，本轮就整体延后到宏任务，避免把排版塞进当前绘制帧。
     */
    private deferFlush = false;

    /**
     * 创建 Vector SVG 连续交互预览管理器。
     *
     * @param identity - 当前编辑器实例标识。
     */
    constructor(identity: string) {
        this.framework = getFramework(identity);
        const { events, frame, flatLayer } = this.framework.service;

        // manager 在插件注册时只创建一次，并与当前 framework 共享生命周期。
        // 框架的 plugin destroy 会在每次 editor.init() 时调用，它只负责重置当前文档状态，不会
        // 重建 manager。因此这些监听不保存 ID，也不在 dispose() 中取消，否则重新初始化后
        // manager 将无法再收到交互事件。framework 整体释放时，EventEmitter 与 manager 会一同回收。
        events.on(Events.SVGPreviewRequest, (request: SVGPreviewRequest) => {
            this.handleRequest(request);
        });

        events.on(Events.DrawableSettled, (layerID: string, ready: boolean) => {
            // 只有 commit 后正在等待正式资源的图层才能释放 Preview，交互进行中到达的 settled 一律忽略。
            //
            // 这里不需要额外的 drawable 版本号：EdgelessObject 的 loadResource 版本校验只让最新一轮
            // 通过 onResourceSettled，因此本事件天然只代表当前 drawable version —— 更晚的构建会让
            // 等待目标自动前推，旧 version 的成功与失败都不会到达这里。
            if (!this.waitingForDrawable.has(layerID)) return;

            this.release(layerID);
            this.publish();

            if (!ready) this.framework.service.edgelessCanvas.renderAll();
        });

        frame.onBeforeBaseOperate((layer, type) => {
            // Frame 传入的是当前操作根图层；Group Resize 时这里会收到 Group，
            // begin 会递归收集其中的 Vector 子图层并分别建立 Preview。
            if (type === BaseLayerViewType.RESIZE) this.begin(layer);
        });

        frame.onBaseOperate((_layerID, type, args) => {
            if ((type === BaseLayerViewType.RESIZE || type === BaseLayerViewType.SCALE) && args.isEnd) this.end();
        });

        flatLayer.onDelete((layerIDs) => {
            layerIDs.forEach((layerID) => this.release(layerID));
            this.publish();
        });
    }

    /**
     * 获取当前已经发布的 Vector SVG 预览列表。
     *
     * 返回缓存数组而不是临时转换 previewCache，以满足 useSyncExternalStore 对稳定 snapshot 引用的要求。
     *
     * @returns 当前预览列表。
     */
    getPreviews = (): VectorLayerSVGPreviewItem[] => this.previews;

    /**
     * 订阅 Vector SVG 预览变化。
     *
     * @param listener - 预览列表变化回调。
     * @returns 取消订阅函数。
     */
    subscribe = (listener: () => void): (() => void) => {
        this.subscribers.add(listener);

        return () => {
            this.subscribers.delete(listener);
        };
    };

    /**
     * 开始接管目标 Vector Layer 或 Group 中的 Vector 子图层。
     *
     * 只有能同步取得 ready 素材并完成 builder 的图层才进入 active，避免首次 Polygon / Graph
     * 加载时 preview 隐藏框架占位图，却又没有可以展示的 SVG。
     *
     * @param layer - 当前操作的根图层。
     * @returns 无返回值。
     */
    private begin(layer: ILayerProps): void {
        this.active.forEach((layerID) => this.release(layerID));

        // SVGPreviewRequest 始终传入具体 Vector Layer，但 Frame 的 Resize 生命周期可能传入 Group 根图层。
        // 因此统一使用 collectVectorLayers 展开目标：Vector 返回自身，Group 返回其所有 Vector 子图层。
        collectVectorLayers(layer).forEach((item) => {
            const object = this.framework.service.edgelessCanvas.getByLayerId(item.id) as IEdgelessVectorLayer | null;
            if (!object) return;

            const result = object.resolveRuntimeSVGResult(item);
            if (!result) return;

            this.active.add(item.id);
            this.framework.service.events.emit(Events.SVGPreviewActiveChange, item.id, true);

            // active change 只改变渲染跳过条件，不会主动标记 Canvas。
            // 这里进行一次 base-info dirty，使下一帧先隐藏旧 drawable，再由 Stage 插件展示 SVG。
            this.framework.service.edgelessCanvas.setDirty({ object, onlyUpdateBaseInfo: true });
            this.previewCache.set(item.id, {
                layer: item,
                renderBox: result.renderBox,
                svg: result.svg,
            });
        });

        this.publish();

        // 这里调用 renderAll 用于立即把原来的 Canvas drawable 从画面上移除，让 SVG Preview 接管显示。
        if (this.active.size > 0) this.framework.service.edgelessCanvas.renderAll();
    }

    /**
     * 标记连续交互结束，并等待每个图层的最新正式 drawable settled。
     * 此时 SVG Preview 仍然保持激活；每个图层只会在 DrawableSettled 到达后单独释放。
     *
     * @param layerID - 只结束该图层的会话；省略时结束全部 active 图层。
     * @returns 无返回值。
     */
    private end(layerID?: string): void {
        if (layerID) {
            if (this.active.has(layerID)) this.waitingForDrawable.add(layerID);
            return;
        }

        // Frame 的 Resize / Scale 生命周期结束时没有单一目标：Group 交互中全部 active 子图层
        // 同时结束，各自等待自己的正式 drawable。
        this.active.forEach((item) => this.waitingForDrawable.add(item));
    }

    /**
     * 清理预览缓存和未执行的动画帧。
     *
     * 框架的 plugin destroy 在文档重载时也会触发，因此保留事件监听，只重置本轮预览状态，
     * 与 Text SVG Preview 的生命周期保持一致。
     *
     * @returns 无返回值。
     */
    dispose(): void {
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
        if (this.flushTimer) window.clearTimeout(this.flushTimer);

        this.animationFrame = 0;
        this.flushTimer = 0;
        this.deferFlush = false;

        this.active.forEach((layerID) => this.release(layerID));
        this.pendingLayers.clear();
        this.waitingForDrawable.clear();
        this.publish();
    }

    /**
     * 处理 Vector API、Behavior 或文字编辑会话发出的 preview 通知。
     *
     * 各阶段语义定义在 `SVGPreviewRequest.phase`；这里只实现它：`update` 开始或继续一次会话并
     * 取消上一轮交接等待，`refresh` 按最新输入重画（必要时接管）但不动交接等待，
     * `commit` 结束目标图层的会话并等待正式资源接管。
     *
     * @param request - 连续交互阶段和当前图层。
     * @returns 无返回值。
     */
    private handleRequest(request: SVGPreviewRequest): void {
        const { phase, layer } = request;

        if (phase === 'commit') {
            // commit 事件先于最终正式更新发出。Vector loadResource 是异步流程，
            // 因此 waitingForDrawable 会在 drawable settled 前可靠建立，preview 仍会保留到正式资源 ready。
            // 只等待被提交的这一层：同帧其它 active 图层各自等待自己的正式 drawable。
            this.end(layer.id);
            return;
        }

        // 首次请求由 begin 同步构建并发布当前图层的 SVG。这里直接返回，
        // 避免把同一份图层数据再加入 pendingLayers，导致下一动画帧重复构建。
        //
        // refresh 也允许在此接管：进入已有文字的 Vector 编辑态时，首份组合可能因素材或字体尚未
        // 就绪而无法构建，Text Render Invalidation 后必须立刻接管而不是等到首次输入。begin 自身要求同步组合
        // 结果可用，因此 pending / failed 依然不会隐藏 Canvas。refresh 只由持有会话的 Text Editor
        // 发出，退出时一定会发 commit，因此这里接管不会产生等不到 commit 的常驻 Preview。
        if (!this.active.has(layer.id)) {
            this.begin(layer);
            return;
        }

        // commit 后 Preview 会继续保持 active，直到异步正式 drawable settled。如果此期间又收到
        // update，表示新一轮交互已经开始；必须取消上一轮的等待标记，否则上一轮的
        // DrawableSettled 到达时会误调用 release，关闭新一轮交互正在使用的 Preview。
        //
        // refresh 不属于新一轮交互：它只是用最新输入重画当前会话，因此必须保留等待标记，
        // 否则 Text Render Invalidation 会让已经 commit 的交接永远等不到释放时机。
        if (phase === 'update') this.waitingForDrawable.delete(layer.id);

        this.schedule(layer);
    }

    /**
     * 把最新图层数据合并进当前待构建队列，并按是否含文字选择 flush 时机。
     *
     * @param layer - 本轮需要重新构建的最新图层数据。
     * @returns 无返回值。
     */
    private schedule(layer: ILayerProps): void {
        // 同一动画帧内的重复请求只保留每个图层的最新状态，避免连续交互
        // 每次回调都同步构建 SVG。
        this.pendingLayers.set(layer.id, layer);

        if ((layer.data as IVectorLayerProps).textInfo?.text) this.deferFlush = true;

        // animationFrame 或 flushTimer 有值表示当前已预约本轮 flush。
        // pendingLayers 已经用最新 layer 覆盖旧数据，因此无需重复预约；
        // 已存在的回调会在本轮统一消费最新数据。
        if (this.animationFrame || this.flushTimer) return;

        this.animationFrame = requestAnimationFrame(() => {
            // 当前预约已经开始执行，先重置 ID，使之后到达的请求可以预约新的绘制帧。
            this.animationFrame = 0;

            if (!this.deferFlush) {
                this.flush();
                return;
            }

            this.flushTimer = window.setTimeout(() => {
                this.flushTimer = 0;
                this.flush();
            }, 0);
        });
    }

    /**
     * 消费每层在当前动画帧累积的最新数据，并发布新的 SVG DOM。
     *
     * @returns 无返回值。
     */
    private flush(): void {
        this.deferFlush = false;

        this.pendingLayers.forEach((layer, layerID) => {
            const object = this.framework.service.edgelessCanvas.getByLayerId(layerID) as IEdgelessVectorLayer | null;
            if (!object) return;

            const result = object.resolveRuntimeSVGResult(layer);
            if (!result) return;

            this.previewCache.set(layerID, {
                layer,
                renderBox: result.renderBox,
                svg: result.svg,
            });
        });

        // pendingLayers 只是当前动画帧的待构建队列，成功结果已写入 previewCache，因此本轮
        // 同步消费完后必须整体清空，避免下一帧重复构建未变化的图层并持续引用旧数据。
        // 构建失败的条目也不保留：保留它不会自动预约新的 RAF，后续 update 会按最新数据重新入队。
        this.pendingLayers.clear();

        this.publish();
    }

    /**
     * 释放单个 Vector Layer 的 preview 接管状态。
     *
     * 本方法只修改 manager 内部状态，不直接通知 React。调用方必须在单次或批量释放完成后
     * 调用 publish()，使批量操作只发布一次最终快照，避免 React 观察到中间状态。
     *
     * @param layerID - 需要释放的 Vector Layer ID。
     * @returns 无返回值。
     */
    private release(layerID: string): void {
        this.framework.service.events.emit(Events.SVGPreviewActiveChange, layerID, false);

        this.active.delete(layerID);
        this.waitingForDrawable.delete(layerID);
        this.pendingLayers.delete(layerID);
        this.previewCache.delete(layerID);
    }

    /**
     * 发布最新预览列表并通知 React Stage 订阅者。
     *
     * @returns 无返回值。
     */
    private publish(): void {
        this.previews = [...this.previewCache.values()];
        this.subscribers.forEach((listener) => listener());
    }
}
